import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Camera, ImageIcon, Info, Loader2, UploadCloud } from "lucide-react";

// Iter 80/81: photo min-side is dynamic — the server's `/api/photo-spec`
// endpoint returns the current gate, backed by a two-level `photo_spec`
// setting (year override → district default → env fallback → 224).
// If the fetch fails we default to 224×224 to match the backend when
// unconfigured.
const DEFAULT_MIN_SIDE = 224;
const OUTPUT_QUALITY = 0.92;

// Module-level per-yearId cache so N mounted PhotoUploadCrop instances all
// reuse one network call per scope.
const _photoSpecCache = new Map();  // key -> Promise<{min_width, min_height, min_side}>
function fetchPhotoSpec(yearId) {
    const key = yearId || "_global";
    if (_photoSpecCache.has(key)) return _photoSpecCache.get(key);
    const url = yearId ? `/photo-spec?year_id=${encodeURIComponent(yearId)}` : "/photo-spec";
    const p = api.get(url)
        .then((r) => {
            const mw = Number(r?.data?.min_width) || DEFAULT_MIN_SIDE;
            const mh = Number(r?.data?.min_height) || DEFAULT_MIN_SIDE;
            const side = Number(r?.data?.min_side) || Math.max(mw, mh);
            return { min_width: mw, min_height: mh, min_side: side };
        })
        .catch(() => ({ min_width: DEFAULT_MIN_SIDE, min_height: DEFAULT_MIN_SIDE, min_side: DEFAULT_MIN_SIDE }));
    _photoSpecCache.set(key, p);
    return p;
}

/**
 * Draw the source image into a `size × size` JPEG canvas using the pixel-space
 * crop rectangle returned by react-easy-crop (`onCropComplete`'s second arg).
 * Returns a Blob ready to upload.
 */
async function cropImageToBlob(imageSrc, pixelCrop, size) {
    const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.crossOrigin = "anonymous";
        img.src = imageSrc;
    });
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    // High-quality resampling for the downscale.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    // White background so transparent PNGs don't come out with black edges once
    // saved as JPEG.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, size, size,
    );
    return await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", OUTPUT_QUALITY));
}

/**
 * Reusable photo upload with a fixed 1:1 crop step. Enforces the current
 * server-side minimum source resolution (see `photo_spec`) and always emits
 * a same-side JPEG so the directory print pipeline stays consistent.
 *
 * Props:
 *  - label       optional label above the button (e.g. "Member photo")
 *  - previewSrc  URL to preview when no new file has been chosen
 *  - onCropped   (file: File) => void — invoked with the cropped JPEG File
 *  - onRemove    optional () => void — called when the admin/member removes an existing photo
 *  - testid      base data-testid for automation
 *  - required    if true, the empty-state slot shows a subtle required border
 */
export function PhotoUploadCrop({ label, previewSrc, onCropped, onRemove, testid, required = false, subject = "Member", yearId }) {
    const inputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [rawSrc, setRawSrc] = useState("");
    const [rawName, setRawName] = useState("");
    const [open, setOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [pixelCrop, setPixelCrop] = useState(null);
    const [saving, setSaving] = useState(false);
    const [minWidth, setMinWidth] = useState(DEFAULT_MIN_SIDE);
    const [minHeight, setMinHeight] = useState(DEFAULT_MIN_SIDE);
    const minSide = Math.max(minWidth, minHeight);

    // Sync min-dims with server-side photo_spec on mount (or when the scope
    // changes). Falls back to 224×224 on any error.
    useEffect(() => {
        let alive = true;
        fetchPhotoSpec(yearId).then((s) => {
            if (!alive) return;
            setMinWidth(s.min_width);
            setMinHeight(s.min_height);
        });
        return () => { alive = false; };
    }, [yearId]);

    const openPicker = () => inputRef.current?.click();
    const openCamera = () => cameraInputRef.current?.click();

    const onFileChosen = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file later
        if (!file) return;
        if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
            toast.error("Choose a JPG, PNG, WebP, or HEIC image");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const src = reader.result;
            // Enforce minimum source resolution before showing the crop UI
            // so the member sees the error immediately.
            const probe = new Image();
            probe.onload = () => {
                if (probe.naturalWidth < minWidth || probe.naturalHeight < minHeight) {
                    toast.error(`Photo too small (${probe.naturalWidth}×${probe.naturalHeight})`, {
                        description: `Please use a higher quality photo of at least ${minWidth}×${minHeight} pixels.`,
                    });
                    return;
                }
                setRawSrc(src);
                setRawName(file.name || "photo.jpg");
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                setPixelCrop(null);
                setOpen(true);
            };
            probe.onerror = () => toast.error("Could not read the image");
            probe.src = src;
        };
        reader.onerror = () => toast.error("Could not read the file");
        reader.readAsDataURL(file);
    };

    const onCropComplete = useCallback((_areaPct, areaPx) => setPixelCrop(areaPx), []);

    const confirmCrop = async () => {
        if (!pixelCrop) { toast.error("Adjust the crop first"); return; }
        setSaving(true);
        try {
            const blob = await cropImageToBlob(rawSrc, pixelCrop, minSide);
            if (!blob) throw new Error("Crop failed");
            // Rename to .jpg (output is always JPEG). Backend renames files
            // to the canonical Name_Phone scheme; extension only.
            const baseName = (rawName.replace(/\.[^./]+$/, "") || "photo") + ".jpg";
            const cropped = new File([blob], baseName, { type: "image/jpeg", lastModified: Date.now() });
            onCropped?.(cropped);
            setOpen(false);
        } catch {
            toast.error("Could not crop the photo — try another image");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-center gap-4">
            <div className={`h-24 w-24 shrink-0 rounded-2xl overflow-hidden grid place-items-center border ${required && !previewSrc ? "border-rotary-cranberry/50" : "bg-muted/40"}`} data-testid={`${testid}-preview`}>
                {previewSrc
                    ? <img src={previewSrc} alt="" className="h-full w-full object-cover" />
                    : <Camera className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="flex flex-col gap-1.5">
                {label && <span className="label-eyebrow text-[10px]">{label}</span>}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    onChange={onFileChosen}
                    className="hidden"
                    data-testid={`${testid}-input`}
                />
                {/* Dedicated camera input — `capture="environment"` reliably
                    launches the rear camera on Android/iOS. Kept separate
                    from the gallery input so the gallery picker keeps its
                    exact behaviour and only the camera path gets an
                    Android-friendly `accept="image/*"`. */}
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={onFileChosen}
                    className="hidden"
                    data-testid={`${testid}-camera-input`}
                />
                <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" onClick={openPicker}
                        className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                        data-testid={`${testid}-upload-btn`}>
                        <UploadCloud className="mr-1.5 h-3.5 w-3.5" /> {previewSrc ? "Replace photo" : "Upload photo"}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={openCamera}
                        className="rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                        data-testid={`${testid}-camera-btn`}>
                        <Camera className="mr-1.5 h-3.5 w-3.5" /> Camera
                    </Button>
                    {previewSrc && onRemove && (
                        <Button type="button" size="sm" variant="outline"
                            onClick={onRemove}
                            className="rounded-full text-rotary-cranberry border-rotary-cranberry/40 hover:bg-rotary-cranberry hover:text-white"
                            data-testid={`${testid}-remove-btn`}>
                            Remove
                        </Button>
                    )}
                </div>
                <div
                    className="mt-0.5 max-w-[280px] rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2"
                    data-testid={`${testid}-upload-instructions`}
                >
                    <p className="text-xs font-semibold text-amber-900 leading-snug flex items-start gap-1.5">
                        <Info className="h-4 w-4 mt-px shrink-0 text-amber-600" />
                        <span>Upload from gallery or Camera. Upload only passport size photograph and use the crop tool to crop the image.</span>
                    </p>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid={`${testid}-crop-dialog`}>
                    <DialogHeader>
                        <DialogTitle className="rotary-heading text-xl flex items-center gap-2">
                            <ImageIcon className="h-5 w-5 text-rotary-blue" /> Adjust {subject} photo
                        </DialogTitle>
                    </DialogHeader>
                    <div
                        className="rounded-lg border-2 border-rotary-blue/40 bg-rotary-blue/5 px-3 py-2.5"
                        data-testid={`${testid}-crop-instructions`}
                    >
                        <p className="text-sm font-semibold text-rotary-ink leading-snug flex items-start gap-2">
                            <Info className="h-4 w-4 mt-0.5 shrink-0 text-rotary-blue" />
                            <span>Drag the photo to reposition and use the zoom slider (or pinch/scroll) to zoom. Centre the face inside the square — this crop becomes the final passport-size photo.</span>
                        </p>
                    </div>
                    <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-black">
                        {rawSrc && (
                            <Cropper
                                image={rawSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                objectFit="contain"
                                cropShape="rect"
                                showGrid={true}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                minZoom={1}
                                maxZoom={4}
                            />
                        )}
                    </div>
                    <div className="mt-2">
                        <span className="label-eyebrow text-[10px]">Zoom</span>
                        <Slider min={1} max={4} step={0.05}
                            value={[zoom]}
                            onValueChange={(v) => setZoom(v[0])}
                            aria-label="Zoom"
                            data-testid={`${testid}-zoom-slider`} />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline"
                            onClick={() => setOpen(false)}
                            className="rounded-full"
                            data-testid={`${testid}-crop-cancel`}>
                            Cancel
                        </Button>
                        <Button type="button"
                            onClick={confirmCrop}
                            disabled={saving}
                            className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                            data-testid={`${testid}-crop-save`}>
                            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                            Save cropped photo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default PhotoUploadCrop;
